import React from 'react';
import LegalLayout, { LegalSection, LegalSubheading, LegalList } from './LegalLayout';

const TermsOfService = () => {
  return (
    <LegalLayout title="Terms of Service" effectiveDate="May 16, 2026" lastUpdated="May 16, 2026">
      <LegalSection>
        <p>
          By using Got Next ("the App"), operated by Lumenforge Studios LLC ("we," "us"), you agree to these Terms of
          Service. If you do not agree, do not use the App.
        </p>
      </LegalSection>

      <LegalSection heading="1. Eligibility">
        <p>
          You must be at least 13 years old to use Got Next. If you are under 18, you represent that you have permission
          from a parent or legal guardian.
        </p>
      </LegalSection>

      <LegalSection heading="2. Account">
        <p>
          To use Got Next, you sign in with Apple. You are responsible for maintaining the confidentiality of your Apple
          ID credentials and for all activity on your account.
        </p>
      </LegalSection>

      <LegalSection heading="3. Subscription Service — Got Next Pro">
        <p>
          Got Next Pro is an in-app purchase that unlocks additional features (unlimited games and players, heart rate
          tracking, advanced stats, hot streaks, Ball Day Score widget, and more).
        </p>

        <LegalSubheading>Subscription Plans</LegalSubheading>
        <LegalList items={[
          'Monthly — $2.99/month, auto-renewing',
          'Annual — $18.99/year, auto-renewing',
          'Seasonal — $6.99 for 4 months, non-renewing',
        ]} />

        <LegalSubheading>Free Trial (Monthly and Annual only)</LegalSubheading>
        <p>
          New subscribers receive a 14-day free trial of Got Next Pro on Monthly or Annual subscriptions. If you do not
          cancel before the trial ends, your subscription will automatically convert to a paid subscription at the price
          shown when you subscribed.
        </p>

        <LegalSubheading>Auto-Renewal</LegalSubheading>
        <LegalList items={[
          'Monthly and Annual subscriptions auto-renew at the end of each billing period unless cancelled at least 24 hours before the period ends.',
          'Your Apple ID will be charged at the price shown at the time of purchase.',
          'Manage or cancel in Settings → Apple ID → Subscriptions on your iPhone.',
        ]} />

        <LegalSubheading>Non-Renewing Seasonal</LegalSubheading>
        <p>
          The Seasonal subscription is a one-time purchase that grants 4 months of Pro access from the purchase date. It
          does not auto-renew. After 4 months, your Pro access will end automatically unless you purchase another product.
        </p>

        <LegalSubheading>Refunds</LegalSubheading>
        <p>
          All sales are final. Refunds are handled by Apple per Apple's Media Services Terms. To request a refund, visit{' '}
          <a href="https://reportaproblem.apple.com" style={{ color: 'var(--color-primary)' }}>reportaproblem.apple.com</a>.
        </p>

        <LegalSubheading>Price Changes</LegalSubheading>
        <p>
          We may change subscription prices at any time. Changes will not affect your current subscription period. You
          will be notified before any price change takes effect for a renewal.
        </p>

        <LegalSubheading>Loss of Pro Access</LegalSubheading>
        <p>
          If your subscription expires or is cancelled, you will lose access to Pro features on new games. Historical Pro
          data (chemistry stats, heart rate records, etc.) generated while you were Pro remains viewable.
        </p>
      </LegalSection>

      <LegalSection heading="4. Acceptable Use">
        <p>You agree not to:</p>
        <LegalList items={[
          'Use the App for any unlawful purpose',
          'Attempt to bypass the subscription system or feature gates',
          'Reverse engineer, decompile, or extract source code from the App',
          "Submit content that is illegal, harassing, defamatory, or infringes others' rights",
          'Impersonate another person',
        ]} />
        <p>We may suspend or terminate your account for violations.</p>
      </LegalSection>

      <LegalSection heading="5. User Content">
        <p>
          You retain ownership of the data you enter (player names, game stats, etc.). By using Got Next, you grant us a
          worldwide, non-exclusive license to use, store, and display your content only for the purpose of providing the
          App's services to you and the league members you choose to share with.
        </p>
      </LegalSection>

      <LegalSection heading="6. Health Data">
        <p>If you enable health data tracking, you acknowledge:</p>
        <LegalList items={[
          'Got Next is not a medical device',
          'Heart rate and calorie data are sourced from Apple HealthKit and may be inaccurate',
          'Got Next is not a substitute for professional medical advice',
          'You should consult a doctor before beginning any new exercise routine',
        ]} />
      </LegalSection>

      <LegalSection heading="7. Apple Watch and HealthKit">
        <p>
          Got Next integrates with Apple Watch and HealthKit. Use of these features is subject to Apple's terms. We are
          not responsible for Apple's services.
        </p>
      </LegalSection>

      <LegalSection heading="8. Disclaimer of Warranty">
        <p>
          The App is provided "as is" and "as available." We disclaim all warranties, express or implied, including
          merchantability, fitness for a particular purpose, and non-infringement.
        </p>
      </LegalSection>

      <LegalSection heading="9. Limitation of Liability">
        <p>
          To the maximum extent permitted by law, Lumenforge Studios LLC is not liable for any indirect, incidental,
          special, consequential, or punitive damages arising from your use of the App. Our total liability for any claim
          is limited to the amount you paid us in the 12 months prior to the claim.
        </p>
      </LegalSection>

      <LegalSection heading="10. Termination">
        <p>
          You may stop using the App at any time. We may suspend or terminate your access for any violation of these
          Terms.
        </p>
      </LegalSection>

      <LegalSection heading="11. Governing Law">
        <p>
          These Terms are governed by the laws of the State of Louisiana, United States, without regard to
          conflict-of-laws principles. Any disputes will be resolved in the courts located in Louisiana.
        </p>
      </LegalSection>

      <LegalSection heading="12. Changes">
        <p>
          We may modify these Terms from time to time. Material changes will be announced in the App. Continued use after
          a change indicates acceptance.
        </p>
      </LegalSection>

      <LegalSection heading="13. Contact">
        <p>
          Email: <a href="mailto:support@gotnext.app" style={{ color: 'var(--color-primary)' }}>support@gotnext.app</a>
          <br />
          Lumenforge Studios LLC
        </p>
      </LegalSection>
    </LegalLayout>
  );
};

export default TermsOfService;
